import React from 'react';
import { useTranslation } from 'react-i18next';
import styles from './RecordList.module.css';
import ImageWithFallback from '../GenericComponents/ImageWithFallback';
import * as ccUtils from '../CC/utils';
import { useCCContext } from '../CC/CCContext';
import { getImages } from '../Result/getImages';

interface RecordListProps {
    record: any;
    index: number;
    handleLinkClick: (event: React.MouseEvent, index: number) => void;
}

const RecordList: React.FC<RecordListProps> = ({ record, index, handleLinkClick }) => {
    const { t } = useTranslation();
    const ctx = useCCContext();

    function getDefaultRow(record: any, label: string, fieldName: string, defaultValue: string = ""): JSX.Element | null {
        function getNestedValue(obj: any, path: string): any {
          return path.split('.').reduce((acc, part) => acc && acc[part], obj);
        }
      
        let fieldValue = getNestedValue(record?.data?.record, fieldName);
      
        if (Array.isArray(fieldValue)) {
          fieldValue = fieldValue.join(', ');
        } else if (typeof fieldValue === 'object' && fieldValue !== null) {
          fieldValue = JSON.stringify(fieldValue);
        }
      
        if (!fieldValue && !defaultValue) return null;
      
        return (
          <div className={styles.row}>
            <span className={styles.taglabel}>{label} : </span>
            <span 
              className={styles.tagValue}
              dangerouslySetInnerHTML={{ __html: fieldValue ?? defaultValue }}
            ></span>
          </div>
        );
    }

    function getDefaultRowHyperlink(record: any, label: string, fieldName: string, hyperlinkfield: string = "", defaultValue: string = ""): JSX.Element | null {
        function getNestedValue(obj: any, path: string): any {
            return path.split('.').reduce((acc, part) => acc && acc[part], obj);
        }

        let fieldValue = getNestedValue(record?.data?.record, fieldName);

        const handleOnClick = (label: string, value: string, e: React.MouseEvent) => {
            e.stopPropagation();
            e.preventDefault();
            ccUtils.customSearch(ctx, '/record/' + hyperlinkfield, '', '' + value + '', label + ' : ' + value);
        };

        // If fieldValue doesn't exist and no defaultValue is provided, return null
        if (!fieldValue && !defaultValue) return null;

        return (
            <div className={styles.row}>
                <span className={styles.taglabel}>{label} : </span>
                {fieldValue
                    ? <span className={styles.hyperlink} onClick={(e) => handleOnClick(label, fieldValue, e)}>{fieldValue}</span>
                    : defaultValue}
            </div>
        );
    }

    function getDefaultRowNoFieldName(
        record: any, 
        label: string, 
        fieldName: string, 
        defaultValue: string = ""
        ): JSX.Element | null {
          function getNestedValue(obj: any, path: string): any {
            return path.split('.').reduce((acc, part) => acc && acc[part], obj);
          }
        
          let fieldValue = getNestedValue(record?.data?.record, fieldName);
        
          if (Array.isArray(fieldValue)) {
            fieldValue = fieldValue.join(', ');
          } else if (typeof fieldValue === 'object' && fieldValue !== null) {
            fieldValue = JSON.stringify(fieldValue);
          }
        
          if (!fieldValue && !defaultValue) return null;
        
          return (
            <span
              className={styles.RowNoFieldName}
              dangerouslySetInnerHTML={{ __html: (fieldValue ?? defaultValue) + "&nbsp;&nbsp;&nbsp;" }}
            ></span>
          );
      }
    
    function getThesaurusRows(record: any, label: string, CN: string, minLength: number) {
        const thesTerms = record?.data?.record?.ThesTerm;

        if (!thesTerms) return null;

        // Filter the terms based on the provided CN
        const terms = Array.isArray(thesTerms) ?
            thesTerms.filter((term: any) => term.CN.startsWith(CN)) :
            [thesTerms].filter((term: any) => term.CN.startsWith(CN));

        const rows = terms.map((term: any) => {
            let termElements: JSX.Element[] = [];
            let firstDone = false;

            term.TermPathPart.forEach((part: any) => {
                if (part.CN && part.CN.length >= minLength) {
                    termElements.push(
                        <span>
                            {firstDone && <span className={styles.thesNodeSeperator}>&gt;</span>}
                            <span className={styles.thesNode} onClick={(e) => handleThesaurusNodeClick(e, part.CN, label, part.TermPathPart)}>
                                {part.TermPathPart}
                            </span>
                        </span>
                    );
                    firstDone = true;
                }
            });

            return (
                <div className={styles.row} key={term.CN}>
                    <span className={styles.taglabel}>{label} : </span>
                    {termElements}
                </div>
            );
        });

        if (rows.length === 0) return null;

        return rows;
    }

    function getThesaurusRowsStartLevel(
        record: any,
        label: string,
        CN: string,
        minLength: number,
        startLevel: number = 0
      ) {
        const thesTerms = record?.data?.record?.ThesTerm;
      
        if (!thesTerms) return null;
      
        const terms = Array.isArray(thesTerms)
          ? thesTerms.filter((term: any) => term.CN.startsWith(CN))
          : [thesTerms].filter((term: any) => term.CN.startsWith(CN));
      
        const rows = terms.map((term: any) => {
          let termElements: JSX.Element[] = [];
          let firstDone = false;
      
          const filteredParts = term.TermPathPart.slice(startLevel);
      
          filteredParts.forEach((part: any) => {
            if (part.CN && part.CN.length >= minLength) {
              termElements.push(
                <span key={part.CN}>
                  {firstDone && (
                    <span className={styles.thesNodeSeperator}>&gt;</span>
                  )}
                  <span
                    className={styles.thesNode}
                    onClick={(e) =>
                      handleThesaurusNodeClick(e, part.CN, label, part.TermPathPart)
                    }
                  >
                    {part.TermPathPart}
                  </span>
                </span>
              );
              firstDone = true;
            }
          });
      
          return (
            <div className={styles.row} key={term.CN}>
              <span className={styles.taglabel}>{label} :{" "}</span> {/* Hier de spatie */}
              {termElements}
            </div>
          );
        });
      
        if (rows.length === 0) return null;
      
        return rows;
    }
    
    function getThesaurusRowsByType(record: any, label: string, ThesType: string, minLength: number) {
        //console.log(record);
        const thesTerms = record?.data?.record?.mqb_thesterm;

        if (!thesTerms) return null;

        // Filter the terms based on the provided ThesType
        const terms = Array.isArray(thesTerms) ?
            thesTerms.filter((term: any) => term.Type.trim() === ThesType) :
            [thesTerms].filter((term: any) => term.Type.trim() === ThesType);

        const rows = terms.map((term: any, index) => {
            let termElements: JSX.Element[] = [];
            let firstDone = false;

            term.thesTerms.forEach((part: any, index: number) => {
                if (part.CN && part.CN.length >= minLength) {
                    termElements.push(
                        <span key={index}>
                            {firstDone && <span className={styles.thesNodeSeperator}>&gt;</span>}
                            <span className={styles.thesNode} onClick={(e) => handleThesaurusNodeClick(e, part.CN, label, part.TERM)}>
                                {part.TERM}
                            </span>
                        </span>
                    );
                    firstDone = true;
                }
            });

            return (
                <div className={styles.row} key={index}>
                    <span className={styles.taglabel}>{label} : </span>
                    {termElements}
                </div>
            );
        });

        if (rows.length === 0) return null;

        return rows;
    }


    function handleThesaurusNodeClick(event: React.MouseEvent, CN: string, label: string, term: string): void {
        event.stopPropagation();
        event.preventDefault();
        ccUtils.customSearch(ctx, '/Record/mqb_thesterm/thesTerms/CN', '', '[' + CN + ']', label + ': ' + term);
        //console.log(CN, term);
    }

    function hasFilterValue(record: any, filterField: string, filterValue: string): boolean {
        const fieldData = record?.data?.record?.[filterField];

        if (!fieldData) return false;

        // If fieldData is an array
        if (Array.isArray(fieldData)) {
            return fieldData.includes(filterValue);
        }

        // If fieldData is a single value

        return fieldData === filterValue;
    }

    function getPersonnes(record: any, tag: keyof typeof record.data.record) {
        const elements = record?.data?.record?.[tag];
        if (!elements) return null;

        // Convert to array if not already
        const elementArray = Array.isArray(elements) ? elements : [elements];

        // Sorting logic (if required)
        elementArray.sort((a, b) => {
            const keyA = a.RoleTypeID + ',' + a.DisplayOrder;
            const keyB = b.RoleTypeID + ',' + b.DisplayOrder;
            return keyA.localeCompare(keyB);
        });

        const handleOnClick = (e: React.MouseEvent, name: string) => {
            e.preventDefault();
            e.stopPropagation();
            ccUtils.customSearch(ctx, '/record/creatorname,/record/conxother', '', '[' + name + ']', t('Constituent') + ' : ' + name);
        };

        const rows = elementArray.map((element: any, index) => {
            const { Role, [tag]: tagValue, DisplayDate } = element;
            return (
                <div className={styles.row} key={index}>
                    <span className={styles.taglabel}>{Role} : </span>
                    <span className={styles.authName} onClick={(e) => handleOnClick(e, tagValue)}>{tagValue}</span>
                    {DisplayDate && <span className={styles.authDate}>&nbsp;({DisplayDate})</span>}
                </div>
            );
        });

        if (rows.length === 0) return null;

        return <>
            <div>
                {/* {t("record_personnes_institutions")} */} 
            </div>
            <div>
                {rows}
            </div>
        </>
    }

    
    let imageUrl = `${process.env.REACT_APP_IMAGE_BASE_URL}images/nondisponible.cci&width=220&bg=ffffff`; //todo: noimage
    const allImages = getImages(record);
    if ((allImages as any)["list"]?.length > 0) {
        imageUrl = (allImages as any)["list"][0]?.url;
    }
    const invNo = record?.data?.record?.ObjectNumber;
    const recordDataAsString = JSON.stringify(record?.data?.record, null, 2); // Converts the object to a JSON string with pretty print

    return (
        <div>
            {false && <textarea value={recordDataAsString}></textarea>}

            <a
                href={`${window.location.pathname}?action=search&field=/Record/ObjectNumber&label=N°%20de%20gestion&value=${invNo}`}
                className={styles.RecordList}
                key={index}
                onClick={(event) => handleLinkClick(event, index)}
                onMouseDown={e => e.preventDefault()}
            >
                <div className={styles.listViewPanel}>
                    <div className={styles.ccListviewRecordTable}>
                        <div className={styles.ccListviewRecordImage}>
                            {imageUrl && <ImageWithFallback src={imageUrl} alt="Image" placeholder="no_image.jpg" />}
                        </div>
                        <div className={styles.ccListviewRecordText}>
                            
                            {getDefaultRow(record, t("record_objectnumber"), "objectnumber")}   
                            {getDefaultRow(record, t("record_institution"), "institutions")}
                            {getDefaultRow(record, t("record_institution"), "institution")}
                            {getThesaurusRowsStartLevel(record, t("record_fonds"), "MVW.AAX", 3, 2)}
                            {getDefaultRow(record, t("record_classification"), "classification")}
                            {getDefaultRow(record, t("record_objectname"), "objectname")}
                            {getDefaultRow(record, t("record_title"), "title.title")}
                            {getDefaultRow(record, t("record_dated"), "dated")}
                            {record?.data?.record?.recordtype !== "exhibitions" && getPersonnes(record, "creatorname")}
                            {getDefaultRowHyperlink(record, t("record_auteur"), "auteur.auteur", "auteur")}
                            
                            {/*{getThesaurusRowsStartLevel(record, t("Theme"), "MVW.AAJ", 3, 2)}*/}

                            {/*}Specific for P&I records*/}
                            {/*getDefaultRow(record, t("record_institution"), "institution")*/}
                            {getDefaultRow(record, t("record_displayname"), "displayname")}

                            {/*}Specific for Exh records*/}
                            {getDefaultRow(record, t("record_exhtitle"), "exhtitle")}
                            {getDefaultRowNoFieldName(record, t("record_displaydate"), "displaydate")}

                            {/*}Specific for HistEvent records - worden momenteel niet getoond*/}
                            {getDefaultRow(record, t("record_eventname"), "eventname")}

                            {/*}Specific for Site records*/}
                            {getDefaultRow(record, t("record_sitename"), "sitename")}

                            {getDefaultRow(record, t("record_collection"), "webcollection")}
                            

                        </div>
                    </div>
                </div>
            </a>
        </div>
    );
}

export default RecordList;
